/**
 * MemoComposePanel — Gmail-style floating memo composer (notes and tasks).
 *
 * Supports two modes:
 *   - **Create**: blank or pre-filled note (linked entities, date, etc.)
 *   - **Edit**: fetches existing note by entity_id, pre-fills form, updates on submit
 *
 * In edit mode the panel owns all data fetching — callers just pass
 * `editMemo(entityId)` and the panel resolves everything from the CRM store.
 */

import type { Membership } from 'api-client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks';
import type { MemoComposeConfig } from '@/contexts/MemoComposeContext';
import {
  useMemoCompose,
  useMemoComposeState,
  useSetMemoComposeDraftDue,
} from '@/contexts/MemoComposeContext';
import { useOrg } from '@/contexts/OrgContext';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { ComposeShell } from '@/lib/compose/ComposeShell';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';

import { MemoForm } from './MemoForm';
import type { Note } from './notes.types';
import {
  extractMemoData,
  getMemoAssociationNames,
  isMemoEntityType,
  MEETING_ASSOCIATION_TYPE_NAME,
  type MemoAssociationNames,
  type MemoEntityType,
} from './notes.utils';
import { useMemoCreate } from './useMemoCreate';
import { useMemoRecords } from './useMemoRecords';

/**
 * Association type names managed internally for one note-like family — not
 * shown as linked records.
 */
function internalAssociationTypes(names: MemoAssociationNames): Set<string> {
  return new Set(
    [
      names.creator,
      names.owner,
      names.visibleOrg,
      names.visibleUser,
      names.mentionedUser,
      names.stage,
      MEETING_ASSOCIATION_TYPE_NAME,
    ].filter((name): name is string => name !== null)
  );
}

// ---------------------------------------------------------------------------
// Renderer — mount once in Root.tsx
// ---------------------------------------------------------------------------

export function MemoComposeRenderer() {
  const state = useMemoComposeState();
  const { closeMemoCompose } = useMemoCompose();
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  // Resolve org members once, here at the single mount point that already has
  // org context, and hand them to whichever panel renders. This is why the
  // assignee/owner picker "just works" in the composer without any caller
  // passing `users` — both create and edit modes get it by default.
  const { members: orgUsers } = useOrgMembers(orgId);

  if (!state.isOpen || !orgId) {
    return null;
  }

  return createPortal(
    state.mode === 'edit' ? (
      <MemoEditPanel
        orgId={orgId}
        memoEntityId={state.memoEntityId}
        users={orgUsers}
        onClose={closeMemoCompose}
      />
    ) : (
      <MemoCreatePanel
        orgId={orgId}
        config={state.config}
        users={orgUsers}
        onClose={closeMemoCompose}
      />
    ),
    document.body
  );
}

// ---------------------------------------------------------------------------
// Create panel
// ---------------------------------------------------------------------------

function MemoCreatePanel({
  orgId,
  config,
  users,
  onClose,
}: {
  orgId: number;
  config?: MemoComposeConfig;
  users: Membership[];
  onClose: () => void;
}) {
  const isDirtyRef = useRef(false);
  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  // Publish the in-progress task's due to the calendar's ghost marker.
  // Clear it on unmount as a safety net (closeMemoCompose also clears).
  const setDraftDue = useSetMemoComposeDraftDue();
  useEffect(() => () => setDraftDue(undefined), [setDraftDue]);

  const guardedClose = useCallback(() => {
    if (
      isDirtyRef.current &&
      !confirm('You have unsaved changes. Discard this note?')
    ) {
      return;
    }
    onClose();
  }, [onClose]);

  const {
    handleSubmit,
    isSubmitting,
    submitError,
    userEntityId,
    linkedRecordsSlot,
  } = useMemoCreate({
    orgId,
    config,
    onClose,
    logComponent: 'MemoComposePanel',
  });

  return (
    <ComposeShell dataInstance='memo'>
      <MemoForm
        mode='create'
        entityType={config?.entityType ?? 'ahoy_note'}
        users={users}
        onSubmit={handleSubmit}
        onCancel={guardedClose}
        showCloseButton
        isSubmitting={isSubmitting}
        error={submitError}
        currentUserEntityId={userEntityId}
        slotAfterContent={linkedRecordsSlot}
        initialValues={{
          date: config?.date,
          stageEntityId: config?.stageEntityId,
        }}
        size='lg'
        onDirtyChange={handleDirtyChange}
        onDueChange={setDraftDue}
      />
    </ComposeShell>
  );
}

// ---------------------------------------------------------------------------
// Edit panel — fetches note data internally
// ---------------------------------------------------------------------------

function MemoEditPanel({
  orgId,
  memoEntityId,
  users,
  onClose,
}: {
  orgId: number;
  memoEntityId: string;
  users: Membership[];
  onClose: () => void;
}) {
  const isDirtyRef = useRef(false);
  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  const guardedClose = useCallback(() => {
    if (
      isDirtyRef.current &&
      !confirm('You have unsaved changes. Discard changes?')
    ) {
      return;
    }
    onClose();
  }, [onClose]);

  // Fetch the memo object from the CRM store (reactive — updates via WS)
  const noteObject = useCrmObjectSnapshot(memoEntityId, orgId);

  // The panel edits whichever note-like record it was opened on — a task from
  // the Tasks section edits under the task family. Until the snapshot
  // resolves nothing is written, so the note family is a safe default.
  const recordType: MemoEntityType =
    noteObject && isMemoEntityType(noteObject.entity_type_name)
      ? noteObject.entity_type_name
      : 'ahoy_note';
  const names = getMemoAssociationNames(recordType);

  // Extract Note shape from the CRM object
  const note = useMemo<Note | undefined>(() => {
    if (!noteObject) {
      return undefined;
    }
    return extractMemoData(noteObject, names.owner, names.stage);
  }, [noteObject, names]);

  // Extract visible linked entity IDs (filter out internal associations)
  const internalAssociations = useMemo(
    () => internalAssociationTypes(names),
    [names]
  );
  const linkedEntityIds = useMemo(() => {
    if (!noteObject?.associations) {
      return [];
    }
    return noteObject.associations
      .filter(a => !internalAssociations.has(a.association_type_name))
      .map(a => a.target_entity_id);
  }, [noteObject?.associations, internalAssociations]);

  const { user } = useAuthInfo();
  const currentMember = users.find(u => u.userId === user?.id);
  const currentUserEntityId = currentMember?.entityId;

  const { updateRecordAsync, isUpdating, updateError, togglePrivacyAsync } =
    useMemoRecords({ orgId, users, recordType });

  const handleSubmit = useCallback(
    async (
      title: string,
      content: string,
      date?: string,
      ownerEntityId?: string,
      stageAssociation?: string | null,
      privacy?: 'private' | 'shared'
    ) => {
      const userId = user?.id ? String(user.id) : undefined;
      if (!note || !userId) {
        return;
      }
      try {
        await updateRecordAsync({
          noteEntityId: note.entity_id,
          title,
          content,
          date,
          ownerEntityId,
          originalTitle: note.title,
          originalContent: note.content,
          originalDate: note.date,
          originalDatetime: note.datetime,
          originalOwnerEntityId: note.owner_entity_id,
          lastEventSeq: note.last_event_seq,
          userId,
          stageAssociation: stageAssociation ?? null,
          originalStageAssociation: note.stage_entity_id ?? null,
        });
        // Toggle privacy if changed
        if (privacy && (privacy === 'private') !== note.is_private) {
          await togglePrivacyAsync({
            noteEntityId: note.entity_id,
            targetPrivacy: privacy,
          });
        }
        onClose();
      } catch {
        toast.error("We couldn't save your changes. Try again.");
      }
    },
    [note, user?.id, updateRecordAsync, togglePrivacyAsync, onClose]
  );

  // Linked records slot for edit mode — read-only badges of existing associations
  const linkedRecordsSlot = useMemo(() => {
    if (linkedEntityIds.length === 0) {
      return undefined;
    }
    return (
      <div className='flex items-center gap-1 flex-wrap'>
        {linkedEntityIds.map(eid => (
          <EntityBadgeById
            key={eid}
            entityId={eid}
            orgId={orgId}
            variant='xs'
          />
        ))}
      </div>
    );
  }, [linkedEntityIds, orgId]);

  // Loading state while the note snapshot resolves
  if (!note) {
    return (
      <ComposeShell dataInstance='memo'>
        <div className='flex items-center justify-center h-full'>
          <span className='text-sm text-[var(--color-text-secondary)]'>
            Loading note…
          </span>
        </div>
      </ComposeShell>
    );
  }

  return (
    <ComposeShell dataInstance='memo'>
      <MemoForm
        mode='edit'
        note={note}
        entityType={recordType}
        users={users}
        orgId={orgId}
        currentUserEntityId={currentUserEntityId}
        onSubmit={handleSubmit}
        onCancel={guardedClose}
        showCloseButton
        isSubmitting={isUpdating}
        error={updateError instanceof Error ? updateError : null}
        slotAfterContent={linkedRecordsSlot}
        size='lg'
        onDirtyChange={handleDirtyChange}
      />
    </ComposeShell>
  );
}
